import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import classNames from "classnames";
import { Popover } from "antd";
import get from "lodash/get";

// components
import Link from "@/components/Link";
import HirerHeaderSearch from "@/components/HirerHeaderSearch";
import LastUsedCampaign from "@/components/LastUsedCampaign";

// helpers
import useUserProfile from "@/hooks/useUserProfile";

// interfaces
import { IHeaderNavigation, IRenderLink } from "./IHeaderNavigation";

//assets
import styles from "@/assets/styles/components/Header/HirerHeader/index.module.scss";


const HeaderNavigation = ({ navigationLinks, onClick, isMobile, ...rest }: IHeaderNavigation) => {
  const [visitedCampaignList, setVisitedCampaignList] = useState<{
    id: string | number, name: string, image: string }[]>([]);

  const pathname = usePathname();
  const { data, isAuthorized } = useUserProfile();

  // classNames
  const navigationLinkClassName = styles["locl-hirer-header__navigation-link"];
  const navigationLinkActiveClassName = styles["locl-hirer-header__navigation-link_active"];
  
  // methods
  const getIsActiveRoute = (route: string) => {
    if (route === "/") {
      return pathname === route;
    }

    return pathname.includes(route)
  };

  // useEffect
  useEffect(() => {
    if (data && get(data, "preferences.lastUsedCampaign.id")) {
      const lastUsedCampaign = data.preferences.lastUsedCampaign;

      setVisitedCampaignList([{
        id: lastUsedCampaign.id,
        name: lastUsedCampaign.name,
        image: get(lastUsedCampaign, "photo.url", ""),
      }]);
    }
  }, [data])

  // renders
  const renderLinkContent = ({ label, Icon, badge }: IRenderLink) => (<>
    <div className={styles["locl-hirer-header__navigation-link__icon-wrapper"]}>
      <Icon className={styles["locl-hirer-header__navigation-link__icon"]} />
      { !isMobile && !!badge && (
        <span className={styles["locl-hirer-header__navigation-list__element-badge"]}>
          { +badge > 99 ? "+99" : badge }
        </span>
      ) }
    </div>
    <div className={styles["locl-hirer-header__navigation-link__label-wrapper"]}>
      <p className={styles["locl-hirer-header__navigation-link__label"]}>
        { label }
      </p>
    </div>
    { isMobile && !!badge && (
      <span className={classNames(
        styles["locl-hirer-header__navigation-list__element-badge"],
        styles["locl-hirer-header__navigation-list__element-badge_mobile"]
      )}>
        { +badge > 99 ? "+99" : badge }
      </span>
    ) }
  </>)

  const renderLink = ({ label, route, Icon, badge }: IRenderLink) => {
    if (route === "/campaigns" && !isMobile && isAuthorized && visitedCampaignList.length) {
      return (
        <Popover
          placement="bottom"
          trigger="hover"
          content={() => <LastUsedCampaign visitedCampaignList={visitedCampaignList} />}
          overlayClassName={styles["locl-hirer-header__navigation__popover"]}
          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
          className={classNames(
            navigationLinkClassName,
            { [navigationLinkActiveClassName]: getIsActiveRoute(route) }
          )}
          arrow
        >
          <Link
            href={route}
            aria-label={label}
            className={classNames(
              navigationLinkClassName,
              { [navigationLinkActiveClassName]: getIsActiveRoute(route) }
            )}
            onClick={e => onClick && onClick(e, route)}
          >
            { renderLinkContent({ label, route, Icon, badge }) }
          </Link>
        </Popover>
      )
    }

    return (
      <Link
        href={route}
        aria-label={label}
        className={classNames(
          navigationLinkClassName,
          { [navigationLinkActiveClassName]: getIsActiveRoute(route) }
        )}
        onClick={e => onClick && onClick(e, route)}
      >
        { renderLinkContent({ label, route, Icon, badge }) }
      </Link>
    )
  }

  return (
    <nav className={styles["locl-hirer-header__navigation"]} { ...rest }>
      <ul className={styles["locl-hirer-header__navigation-list"]}>
        {navigationLinks.map(({ label, route, Icon, badge, isWithSearch, isMarginLeft }, index) => (
          <li
            key={`${label}-${route}-${index}`}
            className={classNames(
              styles["locl-hirer-header__navigation-list__element"],
              { [styles["locl-hirer-header__navigation-list__element__margin-left"]]: isMarginLeft }
            )}
          >
            { renderLink({ label, route, Icon, badge }) }
            { isWithSearch && <HirerHeaderSearch placeholder="Quick lookup" fullWidth key="header-search"/> }
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default HeaderNavigation;
